import {
	ChangeDetectorRef,
	Component,
	Input,
	forwardRef
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator } from '@angular/forms';

import { BaseControlValueAccessor } from 'rev-shared/util/BaseControlValueAccessor';
import { FileWrapper } from 'rev-shared/ui/fileUpload/FileWrapper';
import { getDimensions } from 'rev-shared/util/ImageUtils';
import { FileUtil } from 'rev-shared/util/FileUtil';

import { IImageComponentMetadata, IImageData } from './ImageSelector.Contract';

import { ButtonsAngularModule } from '../buttons/ButtonsAngular.Module';
import { DirectivesAngularModule } from 'rev-shared/util/directives/DirectivesAngular.Module';
import { FileUploadAngularModule } from 'rev-shared/ui/fileUpload/FileUpload.AngularModule';
import { FormGroupAngularModule } from 'rev-shared/ui/formGroup/FormGroupAngular.Module';
import { FormsModule } from '@angular/forms';
import { PipesAngularModule } from 'rev-shared/pipes/Pipes.AngularModule';
import { SpinnerAngularModule } from '../spinner/SpinnerAngular.Module';
import { TranslateModule } from '@ngx-translate/core';
import { ValidationAngularModule } from 'rev-shared/ui/validation/ValidationAngular.Module';

let nextId = 1;

@Component({
	selector: 'image-selector',
	templateUrl: './ImageSelector.Component.html',
	styleUrls: ['./image-selector.ng.less'],
	host: {
		'class': 'root'
	},
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => ImageSelectorComponent),
		multi: true
	}, {
		provide: NG_VALIDATORS,
		useExisting: forwardRef(() => ImageSelectorComponent),
		multi: true
	}],
	imports: [ButtonsAngularModule,
		DirectivesAngularModule,
		FileUploadAngularModule,
		FormGroupAngularModule,
		FormsModule,
		PipesAngularModule,
		SpinnerAngularModule,
		TranslateModule,
		ValidationAngularModule],
	standalone: true
})
export class ImageSelectorComponent extends BaseControlValueAccessor<IImageData> implements Validator {
	@Input() public defaultImage: { name: string; url: string };
	@Input() public imageComponentMetadata: IImageComponentMetadata;
	@Input() public requireSquareDimensions: boolean;
	@Input() public label: string;
	@Input() public fileExtensions: string[] = FileUtil.imageFileExtensions;
	@Input() public isSmallLabel: boolean;

	public readonly labelId = `image-selector-${nextId++}`;

	public processing: boolean;
	public control: FormControl;

	constructor(
		public ChangeDetectorRef: ChangeDetectorRef
	) {
		super();
	}

	public get labelClass(): string {
		return this.label ? `required-ct` : `required-ct required-indicator`;
	}

	public writeValue(val: IImageData): void {
		super.writeValue(!val?.url ? null : {
			name: val.name || 'image',
			url: val.url
		});
	}

	public validate(ctrl: FormControl): any {
		if(!(ctrl as any).dummy) {
			this.control = ctrl; //Form group directive calls validator with dummy ctrl
		}

		const imgData: IImageData = ctrl.value;
		const fileWrapper = imgData?.fileWrapper;

		const err = {
			required: this.imageComponentMetadata.requiredValidation && !imgData?.url,
			fileType: fileWrapper && !(this.fileExtensions.includes(fileWrapper.extension)),
			size: fileWrapper && !this.isSizeValid(fileWrapper),
			squareDimensions: (imgData && this.requireSquareDimensions) && (imgData.height !== imgData.width)
		};

		return (err.required || err.fileType || err.size || err.squareDimensions) ? err : null;
	}

	public clearSelectedImage(): void {
		this.updateModelValue(null);
	}

	public setDefaultImage(): void {
		this.updateModelValue(null);
	}

	public setImageFile(file: FileWrapper): void {
		this.processing = true;
		this.getImageData(file)
			.then(imageData => this.updateModelValue(imageData))
			.catch(e => console.log('ImageSelector error: ', e))
			.finally(() => {
				this.processing = false;
				this.ChangeDetectorRef.detectChanges();
			});
	}

	private getImageData(file: FileWrapper): Promise<IImageData> {
		const result: IImageData = {
			fileWrapper: file,
			name: 'image'
		};

		if(!(this.fileExtensions.includes(file.extension)) || !this.isSizeValid(file)){
			return Promise.resolve(result);
		}

		return file.getImageUrl()
			.then(url => getDimensions(url)
				.then(dimensions => ({
					...result,
					...dimensions,
					url
				})));
	}

	private isSizeValid(file: FileWrapper): boolean {
		return !this.imageComponentMetadata.sizeValidation ||
			file.size <= this.imageComponentMetadata.maxSize;
	}

	public get isFileDisabled(): boolean {
		return this.processing || this.isDisabled;
	}

	public get url(): string {
		return this.value ? this.value.url : this.defaultImage?.url;
	}

	public get isDefaultImageSelected(): boolean {
		return this.value?.url === this.defaultImage?.url;
	}
}
